import {Trans} from '@lingui/macro';
import React from 'react';

import {CustomFieldModel, CustomFieldResourceTypesEnum} from '../../models/api/customFieldModel';
import {CellsSortDirectionsEnum} from '../../models/api/sortOptionModel';
import {SettingsCustomFieldsPagesEnum} from '../../models/url/settingsUrlModel';
import {SettingsResourcesTableColumnsEnum} from '../common/settingsResourcesCommon';

/*
 * Constants.
 */

export enum CustomFieldSortsEnum {
  NAME_ASC = 'name',
  NAME_DESC = '-name',
  TYPE_ASC = 'type',
  TYPE_DESC = '-type',
  CREATED_AT_ASC = 'created_at',
  CREATED_AT_DESC = '-created_at',
}

export const customFieldResourceTypeToFetchPathRouteMap = {
  [CustomFieldResourceTypesEnum.ACCOUNT]: `${SettingsCustomFieldsPagesEnum.ACCOUNTS}/custom_fields`,
  [CustomFieldResourceTypesEnum.CARD]: `${SettingsCustomFieldsPagesEnum.CARDS}/custom_fields`,
  [CustomFieldResourceTypesEnum.INBOX]: `${SettingsCustomFieldsPagesEnum.INBOXES}/custom_fields`,
  [CustomFieldResourceTypesEnum.TEAMMATE]: `${SettingsCustomFieldsPagesEnum.TEAMMATES}/custom_fields`,
};

export const customFieldPageToResourceTypeMap = {
  [SettingsCustomFieldsPagesEnum.ACCOUNTS]: CustomFieldResourceTypesEnum.ACCOUNT,
  [SettingsCustomFieldsPagesEnum.CARDS]: CustomFieldResourceTypesEnum.CARD,
  [SettingsCustomFieldsPagesEnum.INBOXES]: CustomFieldResourceTypesEnum.INBOX,
  [SettingsCustomFieldsPagesEnum.TEAMMATES]: CustomFieldResourceTypesEnum.TEAMMATE,
};

/*
 * Helpers.
 */

export function getCustomFieldsBaseUrl(tabPage: SettingsCustomFieldsPagesEnum) {
  return `/settings/global/custom-fields/${tabPage}`;
}

export function computeSortedColumnFromCustomFieldSort(customFieldSort?: CustomFieldSortsEnum) {
  switch (customFieldSort) {
    case CustomFieldSortsEnum.NAME_ASC:
    case CustomFieldSortsEnum.NAME_DESC:
      return SettingsResourcesTableColumnsEnum.NAME;

    case CustomFieldSortsEnum.CREATED_AT_ASC:
    case CustomFieldSortsEnum.CREATED_AT_DESC:
      return SettingsResourcesTableColumnsEnum.DATE_CREATED;

    case CustomFieldSortsEnum.TYPE_ASC:
    case CustomFieldSortsEnum.TYPE_DESC:
      return SettingsResourcesTableColumnsEnum.TYPE;

    default:
      return undefined;
  }
}

export function computeModelFieldFromSortedColumn(
  sort?: SettingsResourcesTableColumnsEnum,
): keyof CustomFieldModel | undefined {
  if (sort === SettingsResourcesTableColumnsEnum.NAME) {
    return 'name';
  }

  if (sort === SettingsResourcesTableColumnsEnum.TYPE) {
    return 'type';
  }

  if (sort === SettingsResourcesTableColumnsEnum.DATE_CREATED) {
    return 'updatedAt';
  }

  return undefined;
}

export function computeAscOrDescFromCellsSortDirection(direction?: CellsSortDirectionsEnum) {
  if (direction === CellsSortDirectionsEnum.ASCENDING) {
    return 'asc';
  }

  return 'desc';
}

export function toggleCustomFieldSortsEnum(
  tableColumnResource: SettingsResourcesTableColumnsEnum,
  customFieldSort?: CustomFieldSortsEnum,
) {
  switch (tableColumnResource) {
    case SettingsResourcesTableColumnsEnum.NAME:
      return customFieldSort === CustomFieldSortsEnum.NAME_ASC
        ? CustomFieldSortsEnum.NAME_DESC
        : CustomFieldSortsEnum.NAME_ASC;

    case SettingsResourcesTableColumnsEnum.TYPE:
      return customFieldSort === CustomFieldSortsEnum.TYPE_ASC
        ? CustomFieldSortsEnum.TYPE_DESC
        : CustomFieldSortsEnum.TYPE_ASC;

    case SettingsResourcesTableColumnsEnum.DATE_CREATED:
      return customFieldSort === CustomFieldSortsEnum.CREATED_AT_ASC
        ? CustomFieldSortsEnum.CREATED_AT_DESC
        : CustomFieldSortsEnum.CREATED_AT_ASC;

    default:
      return CustomFieldSortsEnum.NAME_ASC;
  }
}

export function parseApiError(err: any) {
  if (!err || err.message === 'transition prevented') {
    return null;
  }

  return (
    err.message ||
    err.data?.message ||
    err.statusText || <Trans id="custom-fields-error-occurred">An error occurred</Trans>
  );
}

export function computeSortOrder(customFieldSort?: CustomFieldSortsEnum) {
  if (!customFieldSort) {
    return undefined;
  }

  if (
    [
      CustomFieldSortsEnum.NAME_ASC,
      CustomFieldSortsEnum.TYPE_ASC,
      CustomFieldSortsEnum.CREATED_AT_ASC,
    ].includes(customFieldSort)
  ) {
    return CellsSortDirectionsEnum.ASCENDING;
  }

  return CellsSortDirectionsEnum.DESCENDING;
}
